import { Transition } from '@headlessui/react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react'
import { useWindowSize } from 'usehooks-ts'

import { appName } from '@/config/env'
import navigation from '@/config/navigation'
import clsxm from '@/utils/clsxm'

import IconEn from '~/assets/icons/icon-en.svg'
import IconId from '~/assets/icons/icon-id.svg'
import LogoBlack from '~/assets/images/logo-black.png'
import LogoWhite from '~/assets/images/logo-white.png'

type HeaderProps = React.ComponentPropsWithoutRef<'div'>

const Header: FC<HeaderProps> = ({ className }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { width } = useWindowSize()
  const [lang, setLang] = useState(router.locale)
  const [menuOpen, setMenuOpen] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [selectedMenu, setSelectedMenu] = useState('')
  const [navScroll, setNavScroll] = useState(false)

  const isTransparent = useMemo(
    () => router.pathname == '/' && !(navScroll || menuOpen),
    [router.pathname, navScroll, menuOpen]
  )

  const handleChangeLang = async () => {
    const { pathname, asPath, query } = router
    let locale = ''
    if (lang == 'en') {
      locale = 'id'
      setLang('id')
    } else {
      locale = 'en'
      setLang('en')
    }
    setDisabled(true)

    await Promise.all([
      await router.push({ pathname, query }, asPath, { locale, shallow: true }),
      await router.reload(),
    ])
  }

  const listenScrollEvent = () => {
    window.scrollY > 10 ? setNavScroll(true) : setNavScroll(false)
  }

  useEffect(() => {
    window.addEventListener('scroll', listenScrollEvent)
    return () => {
      window.removeEventListener('scroll', listenScrollEvent)
    }
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [router])

  return (
    <>
      <header
        className={clsxm(
          'fixed z-menu h-14 w-screen bg-white shadow-navbar backdrop-blur-sm transition-all duration-300 lg:h-18',
          isTransparent && 'lg:bg-transparent lg:shadow-none',
          className
        )}
      >
        <div className='relative z-20 mx-4 flex h-full items-center justify-between lg:mx-[44px]'>
          <div className='flex lg:flex-1'>
            <Link href='/'>
              <img
                alt={appName}
                src={LogoBlack.src}
                className={clsxm('h-6 lg:h-[38px]', isTransparent && 'lg:hidden')}
              />
              <img
                alt={appName}
                src={LogoWhite.src}
                className={clsxm('hidden h-6 lg:h-[38px]', isTransparent && 'lg:block')}
              />
            </Link>
          </div>

          <div className='flex items-center gap-4'>
            <label className='relative inline-flex cursor-pointer items-center'>
              <input
                type='checkbox'
                value=''
                className={clsxm('peer sr-only', disabled ? 'disabled:cursor-not-allowed' : '')}
                checked={lang == 'en'}
                onChange={handleChangeLang}
                disabled={disabled}
              />
              <div
                className={clsxm(
                  'peer relative h-6 w-[42px] rounded-full bg-[#202020]/10 lg:h-8 lg:w-[58px]',
                  isTransparent && 'lg:bg-white/25'
                )}
              >
                <IconEn
                  className={clsxm(
                    'absolute right-1 top-1 h-4.5 w-4.5 transition-all duration-300 lg:h-6 lg:w-6',
                    lang != 'en' && 'opacity-0'
                  )}
                />
                <IconId
                  className={clsxm(
                    'absolute left-1 top-1 h-4.5 w-4.5 transition-all duration-300 lg:h-6 lg:w-6',
                    lang == 'en' && 'opacity-0'
                  )}
                />
              </div>
            </label>

            <button
              type='button'
              className='inline-flex items-center justify-center rounded-md outline-none'
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span className='sr-only'>Open main menu</span>
              <span
                className={clsxm(
                  'text-2xl text-black lg:text-[38px]',
                  menuOpen ? 'kek-close' : 'kek-hamburger',
                  isTransparent && 'lg:text-white'
                )}
                aria-hidden='true'
              />
            </button>
          </div>
        </div>
      </header>

      <Transition
        show={menuOpen}
        as={Fragment}
      >
        <div
          className={clsxm(
            'fixed inset-0 top-14 z-menu h-full animate-fade-in bg-gray-1/50 transition-all duration-300 lg:top-18',
            width < 1024 && 'w-screen'
          )}
        >
          <div className='flex h-full w-full flex-row-reverse'>
            <nav className='relative z-20 h-full w-full animate-slide-in-right overflow-y-auto bg-gray-1 py-8 pl-[23px] pr-[22px] transition-all duration-300 lg:w-1/3 lg:px-[44px] lg:py-12.5'>
              <ul className='flex flex-col gap-y-8'>
                {navigation.map((item) => (
                  <Fragment key={item.id}>
                    {item.children.map((menu) => (
                      <Fragment key={menu.id}>
                        {menu.children.length == 0 && (
                          <li
                            className={clsxm(
                              'relative flex w-full items-center justify-end py-2 text-2xl font-bold uppercase leading-8 text-neutral-placeholder hover:text-white ',
                              (menu.active.includes(router.pathname) || router.pathname.includes(menu.id)) &&
                                'bg-default before:bg-primary-500 pl-5 before:absolute before:left-0 before:h-9 before:w-1 before:rounded-b before:rounded-t',
                              selectedMenu != '' && selectedMenu != menu.id && 'hidden lg:flex'
                            )}
                          >
                            <Link href={String(t(menu?.path))}>{t(menu?.title)}</Link>
                          </li>
                        )}

                        {menu.children.length > 0 && (
                          <li className={clsxm(selectedMenu != '' && selectedMenu != menu.id && 'hidden lg:flex')}>
                            <button
                              type='button'
                              className={clsxm(
                                'relative flex w-full flex-row-reverse items-center justify-between py-3 uppercase text-neutral-placeholder',
                                (menu.active.includes(router.pathname) || router.pathname.includes(menu.id)) &&
                                  'bg-default before:bg-primary-500 before:absolute before:left-0 before:h-9 before:w-1 before:rounded-b before:rounded-t',
                                menu.id == selectedMenu && 'text-orange',
                                selectedMenu != '' && selectedMenu != menu.id && 'hidden lg:flex',
                                selectedMenu != menu.id && 'hover:text-white'
                              )}
                              onClick={() => {
                                if (menu.id == selectedMenu) {
                                  setSelectedMenu('')
                                } else {
                                  setSelectedMenu(menu.id)
                                }
                              }}
                            >
                              <span className='text-right text-xl font-bold leading-8 lg:text-2xl'>
                                {t(menu?.title)}
                              </span>
                              <span
                                className={clsxm(
                                  'text-[28px]',
                                  menu.id == selectedMenu ? 'kek-close' : 'kek-chevron-left'
                                )}
                              />
                            </button>
                          </li>
                        )}

                        {selectedMenu != '' &&
                          selectedMenu == menu.id &&
                          menu.children.map((child) => (
                            <li
                              key={child.id}
                              className={clsxm(
                                'relative flex w-full items-center justify-end py-3 text-right text-right text-xl font-bold uppercase leading-8 text-neutral-placeholder hover:text-white lg:hidden lg:text-2xl',
                                (child.active.includes(router.pathname) || router.pathname.includes(child.id)) &&
                                  'bg-default before:bg-primary-500 pl-5 before:absolute before:left-0 before:h-9 before:w-1 before:rounded-b before:rounded-t'
                              )}
                            >
                              <Link href={String(t(child?.path))}>{t(child?.title)}</Link>
                            </li>
                          ))}
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
              </ul>
            </nav>

            <Transition
              show={selectedMenu != ''}
              as={Fragment}
            >
              <nav className='relative z-10 hidden h-full w-full animate-open-sub-menu overflow-y-auto bg-[#404040] px-[44px] py-12.5 transition-all lg:block lg:w-1/3'>
                <ul className='space-y-8'>
                  {navigation.map((item) => (
                    <Fragment key={item.id}>
                      {item.children.map((menu) => (
                        <Fragment key={menu.id}>
                          {menu.id == selectedMenu &&
                            menu.children.map((child) => (
                              <li
                                key={child.id}
                                className={clsxm(
                                  'relative flex w-full items-center justify-end py-3 text-right text-2xl font-bold uppercase leading-8 text-neutral-placeholder hover:text-white',
                                  (child.active.includes(router.pathname) || router.pathname.includes(child.id)) &&
                                    'bg-default before:bg-primary-500 pl-5 before:absolute before:left-0 before:h-9 before:w-1 before:rounded-b before:rounded-t'
                                )}
                              >
                                <Link href={String(t(child?.path))}>{t(child?.title)}</Link>
                              </li>
                            ))}
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}
                </ul>
              </nav>
            </Transition>
          </div>
        </div>
      </Transition>
    </>
  )
}

export default Header
